import { apiClient } from "./client";

// OAuth authorization server administration (docs/design/OAUTH-AS.md section 11).
// Hand-written HTTP handlers (not proto), so keys are snake_case. Session only: a
// platform API token is refused. Nothing here ever returns a stored secret or hash;
// the create/rotate calls return a one-time secret that the UI must show once and drop.

export interface OAuthCapabilities {
  platform_admin: boolean;
  manage_agents: boolean;
  view_audit: boolean;
  org_policy: boolean;
}

export interface GrantOrganization {
  organization_id: string;
  organization_name?: string;
  scopes: string[];
  primary: boolean;
  revoked: boolean;
  revoked_reason?: string;
  revoked_at?: string;
}

export interface OrgPolicy {
  organization_id: string;
  organization_name: string;
  allow_connectors: boolean;
  updated_at: string | null;
  updated_by?: string;
}

export interface ConnectedGrant {
  id: string;
  client_id: string;
  client_name: string;
  resource: string;
  resource_name?: string;
  organization_id: string;
  organization_name?: string;
  scopes: string[];
  created_at: string;
  expires_at: string;
  last_used_at?: string;
  revoked: boolean;
  unverified?: boolean;
  /** Every organization of the grant with its own scopes (the fields above are the primary's). */
  organizations: GrantOrganization[];
}

export interface OAuthResource {
  id: string;
  resource_uri: string;
  name: string;
  app_id: string;
  app_name: string;
  scopes: Record<string, string>;
  default_scopes: string[];
  introspection_client_id?: string;
  enabled: boolean;
}

export interface ResourceInput {
  resource_uri?: string;
  name: string;
  app_id?: string;
  scopes: Record<string, string>;
  default_scopes: string[];
  enabled: boolean;
}

export interface OAuthAgent {
  id: string;
  client_id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  resources: string[];
  scopes: string[];
  role_cap: "viewer" | "member" | "admin";
  disabled: boolean;
  disabled_at: string | null;
  created_at: string;
  /** Every organization with its own scope cap; the first is the primary. */
  orgs: AgentOrg[];
}

export interface AgentOrg {
  organization_id: string;
  organization_name?: string;
  scopes: string[];
}

export interface AgentInput {
  name: string;
  orgs: { organization_id: string; scopes: string[] }[];
  resources: string[];
  role_cap: "viewer" | "member" | "admin";
}

/** A secret shown once. Hold it in component state only; never persist it. */
export interface OneTimeSecret {
  client_id: string;
  client_secret: string;
  message: string;
}

export interface DynamicClient {
  client_id: string;
  name: string;
  redirect_uris: string[];
  grant_types: string[];
  registered_ip: string;
  verified: boolean;
  disabled: boolean;
  active_grants: number;
  created_at: string;
  last_used_at: string | null;
}

export interface AuditEvent {
  id: string;
  at: string;
  event: string;
  outcome: string;
  principal_type: string;
  user_id?: string;
  user_email?: string;
  agent_id?: string;
  client_id?: string;
  grant_id?: string;
  resource?: string;
  organization_id?: string;
  organization_ids?: string[];
  scopes: string[];
  ip?: string;
  user_agent?: string;
  detail?: string;
}

export interface AuditFilters {
  client_id?: string;
  user_id?: string;
  agent_id?: string;
  resource?: string;
  organization_id?: string;
  event?: string;
  outcome?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export async function getOAuthCapabilities(): Promise<OAuthCapabilities> {
  const { data } = await apiClient.get<OAuthCapabilities>("/oauth/capabilities");
  return data;
}

export async function listConnectedGrants(): Promise<ConnectedGrant[]> {
  const { data } = await apiClient.get<{ grants: ConnectedGrant[] }>("/oauth/grants");
  return data.grants;
}

export async function revokeConnectedGrant(id: string): Promise<void> {
  await apiClient.delete(`/oauth/grants/${encodeURIComponent(id)}`);
}

export async function revokeConnectedGrantOrg(grantId: string, orgId: string): Promise<void> {
  await apiClient.delete(`/oauth/grants/${encodeURIComponent(grantId)}/orgs/${encodeURIComponent(orgId)}`);
}

export async function getOrgPolicy(orgId: string): Promise<OrgPolicy> {
  const { data } = await apiClient.get<OrgPolicy>(`/oauth/orgs/${encodeURIComponent(orgId)}/policy`);
  return data;
}

export async function setOrgPolicy(orgId: string, allow: boolean): Promise<{ policy: OrgPolicy; grants_affected: number }> {
  const { data } = await apiClient.put<{ policy: OrgPolicy; grants_affected: number }>(`/oauth/orgs/${encodeURIComponent(orgId)}/policy`, {
    allow_connectors: allow,
  });
  return data;
}

export async function listOAuthResources(): Promise<{ resources: OAuthResource[]; can_manage: boolean }> {
  const { data } = await apiClient.get<{ resources: OAuthResource[]; can_manage: boolean }>("/oauth/resources");
  return data;
}

export async function createOAuthResource(input: ResourceInput): Promise<OAuthResource> {
  const { data } = await apiClient.post<OAuthResource>("/oauth/resources", input);
  return data;
}

export async function updateOAuthResource(id: string, input: ResourceInput): Promise<OAuthResource> {
  const { data } = await apiClient.patch<OAuthResource>(`/oauth/resources/${encodeURIComponent(id)}`, input);
  return data;
}

export async function deleteOAuthResource(id: string): Promise<void> {
  await apiClient.delete(`/oauth/resources/${encodeURIComponent(id)}`);
}

export async function provisionIntrospectionClient(id: string): Promise<OneTimeSecret> {
  const { data } = await apiClient.post<OneTimeSecret>(`/oauth/resources/${encodeURIComponent(id)}/introspection-client`);
  return data;
}

export async function listOAuthAgents(): Promise<OAuthAgent[]> {
  const { data } = await apiClient.get<{ agents: OAuthAgent[] }>("/oauth/agents");
  return data.agents;
}

export async function createOAuthAgent(input: AgentInput): Promise<{ agent: OAuthAgent } & OneTimeSecret> {
  const { data } = await apiClient.post<{ agent: OAuthAgent } & OneTimeSecret>("/oauth/agents", input);
  return data;
}

export async function updateOAuthAgent(id: string, input: AgentInput): Promise<OAuthAgent> {
  const { data } = await apiClient.patch<OAuthAgent>(`/oauth/agents/${encodeURIComponent(id)}`, input);
  return data;
}

export async function setOAuthAgentDisabled(id: string, disabled: boolean): Promise<void> {
  await apiClient.post(`/oauth/agents/${encodeURIComponent(id)}/${disabled ? "disable" : "enable"}`);
}

export async function rotateOAuthAgentSecret(id: string): Promise<OneTimeSecret> {
  const { data } = await apiClient.post<OneTimeSecret>(`/oauth/agents/${encodeURIComponent(id)}/rotate-secret`);
  return data;
}

export async function listDynamicClients(): Promise<DynamicClient[]> {
  const { data } = await apiClient.get<{ clients: DynamicClient[] }>("/oauth/clients");
  return data.clients;
}

export async function setClientVerified(clientId: string, verified: boolean): Promise<void> {
  await apiClient.patch(`/oauth/clients/${encodeURIComponent(clientId)}`, { verified });
}

export async function revokeDynamicClient(clientId: string): Promise<{ grants_revoked: number }> {
  const { data } = await apiClient.post<{ grants_revoked: number }>(`/oauth/clients/${encodeURIComponent(clientId)}/revoke`);
  return data;
}

export async function deleteDynamicClient(clientId: string): Promise<void> {
  await apiClient.delete(`/oauth/clients/${encodeURIComponent(clientId)}`);
}

export async function listOAuthAudit(filters: AuditFilters): Promise<{ events: AuditEvent[]; next_cursor: string }> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") params[k] = v;
  }
  const { data } = await apiClient.get<{ events: AuditEvent[]; next_cursor: string }>("/oauth/audit", { params });
  return data;
}

/** Mirrors the server's strict resource URL rule so the form can say what is wrong early.
 *  The server is the authority and re-validates. Returns an error message or null. */
export function resourceUrlProblem(raw: string): string | null {
  if (raw === "") return "Enter the MCP URL.";
  if (raw !== raw.trim()) return "Remove spaces around the URL.";
  if (/[*?#]/.test(raw)) return "Use the exact URL: no wildcard, query or fragment.";
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return "Enter an absolute https URL.";
  }
  if (u.protocol !== "https:") return "Only https URLs are allowed.";
  if (u.username || u.password) return "The URL must not contain credentials.";
  if (["localhost", "127.0.0.1", "[::1]"].includes(u.hostname)) return "A loopback address cannot be a resource.";
  if (raw !== `${u.protocol}//${u.host}${u.pathname === "/" ? "" : u.pathname.replace(/\/$/, "")}` || u.pathname.endsWith("/")) {
    return "Write the URL exactly as the app publishes it: lower-case host, no default port, no trailing slash.";
  }
  return null;
}

/** Scope names written from now on: <app>:<action>, lower case (mirrors the server). */
const SCOPE_NAME_RE = /^[a-z][a-z0-9_-]{0,31}:[a-z0-9][a-z0-9._-]{0,31}$/;
const LEGACY_SCOPE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9:._-]{0,63}$/;

/** Returns what is wrong with a scope name, or null. `existing` names keep the looser legacy rule. */
export function scopeNameProblem(name: string, existing = false): string | null {
  if (name === "") return "Enter a scope name.";
  if (existing ? !LEGACY_SCOPE_NAME_RE.test(name) : !SCOPE_NAME_RE.test(name)) {
    return "Use <app>:<action> in lower case, for example ledger:read.";
  }
  return null;
}

/** Operator-registered client (a registered app that signs people in or asks for tokens). */
export interface OperatorClient {
  client_id: string;
  name: string;
  redirect_uris: string[];
  public: boolean;
  share_orgs: boolean;
  refresh_token: boolean;
  disabled: boolean;
  active_grants: number;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

export interface OperatorClientInput {
  client_id?: string;
  name?: string;
  public?: boolean;
  redirect_uris?: string[];
  add_redirect_uris?: string[];
  remove_redirect_uris?: string[];
  share_orgs?: boolean;
  refresh_token?: boolean;
}

export async function listOperatorClients(): Promise<OperatorClient[]> {
  const { data } = await apiClient.get<{ clients: OperatorClient[] }>("/oauth/operator-clients");
  return data.clients;
}

export async function createOperatorClient(
  input: OperatorClientInput,
): Promise<{ client: OperatorClient; client_secret?: string; message?: string }> {
  const { data } = await apiClient.post<{ client: OperatorClient; client_secret?: string; message?: string }>("/oauth/operator-clients", input);
  return data;
}

export async function updateOperatorClient(clientId: string, input: OperatorClientInput): Promise<OperatorClient> {
  const { data } = await apiClient.patch<OperatorClient>(`/oauth/operator-clients/${encodeURIComponent(clientId)}`, input);
  return data;
}

export async function rotateOperatorClientSecret(clientId: string): Promise<OneTimeSecret> {
  const { data } = await apiClient.post<OneTimeSecret>(`/oauth/operator-clients/${encodeURIComponent(clientId)}/rotate-secret`);
  return data;
}

export async function setOperatorClientDisabled(clientId: string, disabled: boolean): Promise<{ grants_revoked: number }> {
  const { data } = await apiClient.post<{ grants_revoked: number }>(
    `/oauth/operator-clients/${encodeURIComponent(clientId)}/${disabled ? "disable" : "enable"}`,
  );
  return data;
}

export async function deleteOperatorClient(clientId: string): Promise<void> {
  await apiClient.delete(`/oauth/operator-clients/${encodeURIComponent(clientId)}`);
}

export const MAX_REDIRECT_URIS = 10;

/** Mirrors the server's redirect URI rule for early feedback; the server is the authority. */
export function redirectUriProblem(raw: string): string | null {
  if (raw === "") return "Enter a redirect address.";
  if (raw.length > 512) return "Too long (512 characters at most).";
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f-\uffff]/.test(raw)) return "Use printable ASCII without spaces.";
  if (/[*\\#?]/.test(raw)) return "Use the exact address: no wildcard, query or fragment.";
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return "Enter an absolute URL.";
  }
  if (u.username || u.password) return "The address must not contain credentials.";
  const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(u.hostname);
  if (u.protocol === "http:" && !loopback) return "Use https (http is only allowed for localhost).";
  if (u.protocol !== "https:" && u.protocol !== "http:") return "Use https.";
  if (!loopback && (/^[0-9.]+$/.test(u.hostname) || u.hostname.startsWith("[") || !u.hostname.includes("."))) {
    return "Use a public domain name, not an IP address.";
  }
  if (u.host !== raw.slice(raw.indexOf("//") + 2).split("/")[0]) return "Write the host in lower case without the default port.";
  if (/%(2e|2f|5c|00)/i.test(u.pathname) || u.pathname.includes("//") || u.pathname.split("/").some((s) => s === "." || s === "..")) {
    return "The path must not contain dot segments, encoded slashes or empty segments.";
  }
  return null;
}
